import { useCallback, useEffect, useState } from 'react';
import { reportCardAPI, studentAPI } from '../services/api';
import { openBlob, readBlobText, saveBlob } from '../components/documents/documentFiles';
import { Logger } from '../utils/logger';
import { reportCardErrorMessage } from '../utils/reportCards';
import type { ReportCardDocument, ReportCardListItem } from '../types/reportCard';

/**
 * Published report cards for one enrolment. `refreshKey` lets the host screen's
 * pull-to-refresh reload the list along with its own data.
 */
export function useReportCardList(enrollmentId: string | null | undefined, refreshKey = 0) {
  const [cards, setCards] = useState<ReportCardListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!enrollmentId) {
      setCards([]);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    reportCardAPI
      .list(enrollmentId)
      .then(res => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        // Newest period first.
        setCards([...list].sort((a, b) => String(b.endDate ?? '').localeCompare(String(a.endDate ?? ''))));
      })
      .catch(err => {
        if (cancelled) return;
        Logger.error('ReportCards', 'Failed to load published report cards', err);
        setCards([]);
        setError(reportCardErrorMessage(err, 'Could not load report cards.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enrollmentId, refreshKey, reloadTick]);

  const reload = useCallback(() => setReloadTick(t => t + 1), []);

  return { cards, loading, error, reload };
}

/** One published report card, rendered by the server as a document model. */
export function useReportCardDocument(reportCardId: string | null | undefined) {
  const [doc, setDoc] = useState<ReportCardDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!reportCardId) {
      setDoc(null);
      setLoading(false);
      setError('Report card not found.');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    reportCardAPI
      .get(reportCardId)
      .then(res => {
        if (!cancelled) setDoc(res.data ?? null);
      })
      .catch(err => {
        if (cancelled) return;
        Logger.error('ReportCards', 'Failed to load report card', err);
        setDoc(null);
        setError(reportCardErrorMessage(err, 'Could not load this report card.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reportCardId, reloadTick]);

  const reload = useCallback(() => setReloadTick(t => t + 1), []);

  return { doc, loading, error, reload };
}

/**
 * The student's photo for the report card, as a data URI ('' when there is none).
 * Same source and auth as useStudentProfilePhoto: /student/{id}/photo/{version}
 * through the API client, since an <Image> pointed at the [Authorize]d URL gets a 401.
 * The document already carries photoVersion, so no describe call is needed.
 */
export function useReportCardPhoto(studentId?: string | null, photoVersion?: string | null) {
  const [photo, setPhoto] = useState('');

  useEffect(() => {
    setPhoto('');
    if (!studentId || !photoVersion) return;
    let cancelled = false;
    studentAPI
      .getStudentPhotoBlob(studentId, photoVersion, false)
      .then(
        res =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => reject(reader.error ?? new Error('Failed to read photo'));
            reader.onload = () => resolve(String(reader.result ?? ''));
            reader.readAsDataURL(res.data);
          }),
      )
      .then(uri => {
        if (!cancelled) setPhoto(uri);
      })
      .catch(err => {
        // Initials are shown instead - not worth an error on the report card.
        Logger.warn('ReportCards', 'Could not load the student photo', err);
      });
    return () => {
      cancelled = true;
    };
  }, [studentId, photoVersion]);

  return photo;
}

type Notify = (message: string, type: 'success' | 'error') => void;

/** A blob request's error body is itself a Blob, so it is read back before extracting {message}. */
async function describePdfError(err: any, fallback: string): Promise<string> {
  const data = err?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    const text = await readBlobText(data);
    try {
      return reportCardErrorMessage({ response: { data: JSON.parse(text) } }, fallback);
    } catch {
      return text.trim() && text.length < 300 ? text.trim() : fallback;
    }
  }
  return reportCardErrorMessage(err, fallback);
}

/**
 * Downloads (or opens) the server-rendered PDF of a published report card.
 * Native: saved to Downloads on Android / share sheet on iOS, or opened in the
 * device viewer. Web: a browser download, or the browser's PDF viewer in a new tab.
 * `busyId` is `save:<id>` / `open:<id>` so the pressed button can show a spinner.
 */
export function useReportCardPdf(notify: Notify) {
  const [busyId, setBusyId] = useState<string | null>(null);

  const fetchPdf = useCallback(
    async (reportCardId: string, fileName: string, mode: 'save' | 'open') => {
      setBusyId(`${mode}:${reportCardId}`);
      try {
        const response = await reportCardAPI.pdf(reportCardId);
        const type = (response.headers?.['content-type'] as string) || 'application/pdf';
        const blob: Blob =
          response.data instanceof Blob ? response.data : new Blob([response.data], { type } as any);
        const contentType = blob.type || 'application/pdf';
        if (mode === 'save') {
          notify(await saveBlob(blob, fileName, contentType), 'success');
        } else {
          await openBlob(blob, fileName, contentType);
        }
      } catch (err) {
        Logger.error('ReportCards', 'Failed to fetch report card PDF', err);
        notify(await describePdfError(err, 'Could not download the report card. Please try again.'), 'error');
      } finally {
        setBusyId(null);
      }
    },
    [notify],
  );

  const download = useCallback(
    (reportCardId: string, fileName: string) => fetchPdf(reportCardId, fileName, 'save'),
    [fetchPdf],
  );
  const open = useCallback(
    (reportCardId: string, fileName: string) => fetchPdf(reportCardId, fileName, 'open'),
    [fetchPdf],
  );

  return { download, open, busyId };
}
